import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import AcronisIcon from '@liquidweb-dev/undercurrent/icons/platform/Acronis';
import Trash2Icon from '@liquidweb-dev/undercurrent/icons/lucide/Trash2';
import Button from '@liquidweb-dev/undercurrent/Button';
import type { DrawerProps } from '@liquidweb-dev/undercurrent/Drawer';
import DrawerHeader from '@liquidweb-dev/undercurrent/DrawerHeader';
import DrawerContent from '@liquidweb-dev/undercurrent/DrawerContent';
import DrawerFooter from '@liquidweb-dev/undercurrent/DrawerFooter';
import KeyItem from '@liquidweb-dev/undercurrent/KeyItem';
import MeasureBar from '@liquidweb-dev/undercurrent/MeasureBar';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import toast from '@liquidweb-dev/undercurrent/toast';
import useProductFlatDetails from 'modules/queries/product/useFlatDetails';
import useAssetUpdate from 'modules/queries/asset/useUpdate';
import useAssetDetails from 'modules/queries/asset/useDetails';
import type { AssetDetails } from 'modules/queries/asset/useDetails';
import useAcronisUsages from 'modules/queries/acronis/backup/useUsages';
import acronisDetailsAlsowith from 'containers/pages/servers/details/acronisDetailsAlsowith';
import BackupTile from 'containers/pages/servers/BackupsAndImages/BackupsBlock/BackupTile';
import BackupSettingsQuotaSelect from 'containers/pages/servers/BackupsAndImages/BackupsBlock/Settings/QuotaSelect';
import BackupSettingsLoading from 'containers/pages/servers/BackupsAndImages/BackupsBlock/Settings/Loading/Settings';
import formatBytes from 'utility/formatBytes';
import acronisProductMap from 'utility/acronisProductMap';
import useProductConfigIntent from 'modules/customerIntent/productConfiguration';

type UpdateAcronisBackupSettingsProps = {
	assetDetails: AssetDetails;
	onDestroy: () => void;
	onClose: () => void;
} & DrawerProps;
const UpdateAcronisBackupSettings: React.FC<
	UpdateAcronisBackupSettingsProps
> = ({ assetDetails, onDestroy, onClose }) => {
	const queryClient = useQueryClient();

	const [quotaOption, setQuotaOption] = useState<'' | number>('');

	const { data: acronisUsage, isLoading: isAcronisUsageLoading } =
		useAcronisUsages(
			{
				uniq_id: assetDetails?.backupsAndStorage?.acronis?.uniq_id ?? '',
			},
			{ enabled: Boolean(assetDetails?.backupsAndStorage?.acronis?.uniq_id) },
		);

	const { data: acronisAssetDetails, isLoading: isAcronisAssetDetailsLoading } =
		useAssetDetails(
			{
				alsowith: acronisDetailsAlsowith,
				uniq_id: assetDetails?.backupsAndStorage?.acronis?.uniq_id ?? '',
			},
			{ enabled: Boolean(assetDetails?.backupsAndStorage?.acronis?.uniq_id) },
		);
	useProductConfigIntent(acronisAssetDetails?.type);

	const {
		data: acronisProductDetails,
		isLoading: isAcronisProductDetailsLoading,
	} = useProductFlatDetails(
		{
			product_code: acronisProductMap(assetDetails?.type),
		},
		{ enabled: Boolean(assetDetails?.type) },
	);

	const { mutate: updateAsset, isLoading: isUpdateAssetLoading } =
		useAssetUpdate();

	const destination = useMemo(
		() => acronisAssetDetails?.featureDetails?.AcronisStorageDestination,
		[acronisAssetDetails],
	);

	const currentQuota = useMemo(
		() =>
			Object.values(acronisAssetDetails?.featureDetails || {})?.find(
				(item) => item?.parent_ptov_id === destination?.ptov_id,
			),
		[acronisAssetDetails, destination],
	);

	const quotaOptions = useMemo(
		() =>
			acronisProductDetails?.flat_options
				?.find((item) => item?.parent_ptov_id === destination?.ptov_id)
				?.values?.filter((item) => item.public)
				?.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)) || [],
		[acronisProductDetails, destination],
	);

	useEffect(() => {
		if (quotaOption === '' && Boolean(currentQuota?.ptov_id)) {
			setQuotaOption(currentQuota?.ptov_id ?? 0);
		}
	}, [currentQuota, quotaOption]);

	const quotaOptionValue = useMemo(
		() =>
			quotaOptions.find((item) => item.ptov_id === Number(quotaOption))?.value,
		[quotaOption, quotaOptions],
	);

	const percentage = useMemo(() => {
		const selected = Number(quotaOptionValue || '0');
		const max = Number(quotaOptions[quotaOptions.length - 1]?.value || '1');
		return (selected / max) * 100;
	}, [quotaOptionValue, quotaOptions]);

	const handleQuotaOptionChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setQuotaOption(Number(event.target.value));
	};

	const handleSubmit = () => {
		const key = Object.entries(acronisAssetDetails?.featureDetails ?? {})?.find(
			([, value]) => value?.parent_ptov_id === destination?.ptov_id,
		)?.[0];

		if (!key) return;

		updateAsset(
			{
				uniq_id: assetDetails?.backupsAndStorage?.acronis?.uniq_id ?? '',
				features: { [key]: quotaOptionValue },
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries({
						queryKey: ['/acronis/backup/usages'],
					});

					toast.info('Successfully submitted');
					onClose();
				},
			},
		);
	};

	if (
		isAcronisProductDetailsLoading ||
		isAcronisUsageLoading ||
		isAcronisAssetDetailsLoading
	) {
		return <BackupSettingsLoading />;
	}

	return (
		<>
			<DrawerHeader
				onClose={onClose}
				description={
					<Typography
						variant="body-sm"
						sx={({ vars }) => ({ color: vars.palette.uc.text.medium })}
					>
						Configure your backup solution to protect your data
					</Typography>
				}
			>
				Backup settings
			</DrawerHeader>
			<DrawerContent sx={{ gap: 300 }}>
				<BackupTile
					label="Acronis Cyber Backups"
					description="Acronis Cloud delivers ultra-fast, secure, and flexible backup and recovery with military-grade encryption, unmatched speed, and complete control over all your data—anytime, anywhere."
					icon={
						<AcronisIcon
							sx={({ size }) => ({
								height: size.sm,
								width: size.sm,
							})}
						/>
					}
					sx={{ border: 'none', padding: 0 }}
				/>
				<Stack spacing={300} flex={1}>
					<>
						<Stack direction="row" spacing={100}>
							<KeyItem
								label="Stored on"
								value={destination?.description}
								sx={{ flex: 1 }}
							/>
							<KeyItem
								label="Current usage"
								value={
									<Stack direction="row" spacing={50}>
										<Typography
											component="span"
											variant="label-xl"
											sx={{ color: ({ vars }) => vars.palette.uc.text.main }}
											fontWeight={500}
										>
											{
												formatBytes(acronisUsage?.usages?.storage?.value || 0)
													.raw
											}
										</Typography>
										<Typography
											component="span"
											variant="label-xl"
											sx={{
												color: ({ vars }) => vars.palette.uc.text.weak,
											}}
										>
											/{' '}
											{
												formatBytes(
													acronisUsage?.usages?.storage?.quota?.max_usage || 0,
												).withUnit
											}
										</Typography>
									</Stack>
								}
								sx={{ flex: 1 }}
							/>
						</Stack>
						<Stack direction="row" spacing={200}>
							<MeasureBar
								orientation="vertical"
								percentage={percentage}
								height={1}
								minWidth={72}
								hidePercentage
							/>
							{acronisProductDetails && (
								<BackupSettingsQuotaSelect
									acronisProductDetails={acronisProductDetails}
									assetDetails={assetDetails}
									destination={Number(destination?.ptov_id ?? '0')}
									disabled={isUpdateAssetLoading}
									currentQuota={Number(currentQuota?.value ?? '0')}
									onChange={handleQuotaOptionChange}
									value={Number(quotaOption ?? '0')}
								/>
							)}
						</Stack>
					</>
				</Stack>
			</DrawerContent>
			<DrawerFooter spacing={100}>
				<Button
					onClick={handleSubmit}
					loading={isUpdateAssetLoading}
					disabled={
						isAcronisProductDetailsLoading ||
						isAcronisUsageLoading ||
						isAcronisAssetDetailsLoading ||
						quotaOption === Number(currentQuota?.ptov_id)
					}
					size="large"
				>
					Update
				</Button>
				<Button
					onClick={onDestroy}
					disabled={isUpdateAssetLoading}
					variant="secondary"
					size="large"
					color="danger"
					startIcon={
						<Trash2Icon
							sx={(theme) => ({
								height: theme.size.sm,
								width: theme.size.sm,
							})}
						/>
					}
				>
					Remove backups
				</Button>
			</DrawerFooter>
		</>
	);
};

export default UpdateAcronisBackupSettings;
